import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  BarChart3, 
  Search, 
  Filter, 
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  CheckSquare,
  AlertTriangle,
  Umbrella,
  Users,
  TrendingUp,
  TrendingDown,
  Edit,
  Save,
  Loader
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import FormDrawer from './FormDrawer';
import { useSystemSettings } from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

const LeaveBalance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: systemSettings } = useSystemSettings();
  const annualLeaveLimit = systemSettings?.annual_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.annual_leave_limit;
  const casualLeaveLimit = systemSettings?.casual_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.casual_leave_limit;
  const medicalLeaveLimit = systemSettings?.medical_leave_limit ?? SYSTEM_SETTINGS_DEFAULTS.medical_leave_limit;
  const combinedLeavePoolLimit = Math.max(casualLeaveLimit, medicalLeaveLimit);
  const defaultEditFormData = {
    annual_limit: annualLeaveLimit,
    casual_limit: casualLeaveLimit,
    medical_limit: medicalLeaveLimit,
    annual_consumed: 0,
    casual_consumed: 0,
    medical_consumed: 0,
  };
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editFormData, setEditFormData] = useState(defaultEditFormData);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const fetchLeaveBalances = useCallback(async () => {
    if (user?.role !== 'admin') {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get('/api/leaves/balance/all');
      setLeaveBalances(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leave balances:', error);
      toast.error('Failed to fetch leave balance data');
      setLeaveBalances([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchLeaveBalances();
  }, [fetchLeaveBalances]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy, sortOrder]);

  // Check if user is admin, if not show error message
  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">Access Denied</h3>
            <p className="text-secondary-600">
              You don't have permission to access this page. Only administrators can view leave balances.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('name');
    setSortOrder('asc');
  };

  const handleEditUser = async (balance) => {
    setEditingUserId(balance.user_id);
    try {
      setEditingUser(balance);
      setEditFormData({
        annual_limit: balance.annual_limit,
        casual_limit: balance.casual_limit,
        medical_limit: balance.medical_limit,
        annual_consumed: balance.annual_consumed,
        casual_consumed: balance.casual_consumed,
        medical_consumed: balance.medical_consumed
      });
      setShowEditModal(true);
    } finally {
      setEditingUserId(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingUser) return;

    // Frontend validation
    if (editFormData.annual_consumed < 0) {
      toast.error('Annual consumed cannot be negative');
      return;
    }
    if (editFormData.casual_consumed < 0) {
      toast.error('Casual consumed cannot be negative');
      return;
    }
    if (editFormData.medical_consumed < 0) {
      toast.error('Medical consumed cannot be negative');
      return;
    }
    if (editFormData.annual_consumed > editFormData.annual_limit) {
      toast.error(`Annual consumed (${editFormData.annual_consumed}) cannot exceed annual limit (${editFormData.annual_limit})`);
      return;
    }
    if (editFormData.casual_consumed > editFormData.casual_limit) {
      toast.error(`Casual consumed (${editFormData.casual_consumed}) cannot exceed casual limit (${editFormData.casual_limit})`);
      return;
    }
    if (editFormData.medical_consumed > editFormData.medical_limit) {
      toast.error(`Medical consumed (${editFormData.medical_consumed}) cannot exceed medical limit (${editFormData.medical_limit})`);
      return;
    }
    
    const combinedCasualMedical = editFormData.casual_consumed + editFormData.medical_consumed;
    if (combinedCasualMedical > combinedLeavePoolLimit) {
      toast.error(`Combined casual and medical consumed (${combinedCasualMedical}) cannot exceed ${combinedLeavePoolLimit} days total`);
      return;
    }

    setEditLoading(true);
    try {
      // Only send consumed amounts, not limits
      const updateData = {
        annual_consumed: editFormData.annual_consumed,
        casual_consumed: editFormData.casual_consumed,
        medical_consumed: editFormData.medical_consumed
      };
      await axios.put(`/api/leaves/balance/${editingUser.user_id}`, updateData);
      toast.success('Leave balance updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      fetchLeaveBalances(); // Refresh the data
    } catch (error) {
      console.error('Failed to update leave balance:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update leave balance');
      }
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditingUserId(null);
    setEditFormData({ ...defaultEditFormData });
  };

  const getCategoryColor = (remaining, limit) => {
    const percentage = (remaining / limit) * 100;
    if (percentage > 50) return 'text-green-600';
    if (percentage > 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTotalRemainingColor = (total) => {
    if (total > 20) return 'bg-green-100 text-green-800';
    if (total > 10) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusIcon = (remaining, limit) => {
    const percentage = (remaining / limit) * 100;
    if (percentage > 50) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (percentage > 25) return <TrendingDown className="h-4 w-4 text-yellow-600" />;
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  };

  const filteredAndSortedBalances = leaveBalances
    .filter(balance => {
      const matchesSearch = !searchTerm || 
        balance.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        balance.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !selectedCategory || 
        (selectedCategory === 'low_annual' && balance.annual_remaining <= 3) ||
        (selectedCategory === 'low_medical_casual' && (combinedLeavePoolLimit - (balance.casual_consumed + balance.medical_consumed)) <= 3) ||
        (selectedCategory === 'low_total' && balance.total_remaining <= 10);
      
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.user_name || '';
          bValue = b.user_name || '';
          break;
        case 'email':
          aValue = a.user_email || '';
          bValue = b.user_email || '';
          break;
        case 'annual_remaining':
          aValue = a.annual_remaining;
          bValue = b.annual_remaining;
          break;
        case 'medical_casual_remaining':
          aValue = combinedLeavePoolLimit - (a.casual_consumed + a.medical_consumed);
          bValue = combinedLeavePoolLimit - (b.casual_consumed + b.medical_consumed);
          break;
        case 'total_remaining':
          aValue = a.total_remaining;
          bValue = b.total_remaining;
          break;
        default:
          aValue = a.user_name || '';
          bValue = b.user_name || '';
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedBalances.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBalances = filteredAndSortedBalances.slice(startIndex, endIndex);

  const categoryFilters = [
    { value: '', label: 'All Categories' },
    { value: 'low_annual', label: 'Low Annual Leave (≤3 days)' },
    { value: 'low_medical_casual', label: 'Low Medical/Casual Leave (≤3 days)' },
    { value: 'low_total', label: 'Low Total Leave (≤10 days)' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Employee Name' },
    { value: 'email', label: 'Email' },
    { value: 'annual_remaining', label: 'Annual Leave Remaining' },
    { value: 'medical_casual_remaining', label: 'Medical/Casual Leave Remaining' },
    { value: 'total_remaining', label: 'Total Leave Remaining' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Leave Management</h1>
            <p className="text-secondary-600">
              Monitor and manage team leave balances
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/leave-management')}
            className="px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
          >
            ← Back to Leave Management
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search by employee name or email..."
                className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Sort */}
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              {sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {/* Clear Filters */}
          {(searchTerm || selectedCategory || sortBy !== 'name' || sortOrder !== 'asc') && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Filter by Low Balance
                </label>
                <select
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categoryFilters.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600">Total Employees</p>
              <p className="text-lg font-semibold text-secondary-900">{leaveBalances.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckSquare className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600">Good Balance</p>
              <p className="text-lg font-semibold text-secondary-900">
                {leaveBalances.filter(b => b.total_remaining > 15).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600">Low Balance</p>
              <p className="text-lg font-semibold text-secondary-900">
                {leaveBalances.filter(b => b.total_remaining <= 10).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Umbrella className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600">Avg. Remaining</p>
              <p className="text-lg font-semibold text-secondary-900">
                {leaveBalances.length > 0 
                  ? Math.round(leaveBalances.reduce((sum, b) => sum + b.total_remaining, 0) / leaveBalances.length)
                  : 0} days
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Balance List */}
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredAndSortedBalances.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No leave balance data found</h3>
            <p className="text-secondary-600">
              {searchTerm || selectedCategory
                ? 'Try adjusting your filters to see more results.'
                : 'No employees found or no leave data available.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Annual Leave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Medical/Casual Leave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Total Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {currentBalances.map(balance => (
                  <tr key={balance.user_id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {balance.user_name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-secondary-900">
                            {balance.user_name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-secondary-500">
                            {balance.user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary-900">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${getCategoryColor(balance.annual_remaining, balance.annual_limit)}`}>
                            {balance.annual_remaining}
                          </span>
                          <span className="text-secondary-500">/ {balance.annual_limit}</span>
                        </div>
                        <div className="text-xs text-secondary-500">
                          Used: {balance.annual_consumed} days
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-secondary-900">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${getCategoryColor(combinedLeavePoolLimit - (balance.casual_consumed + balance.medical_consumed), combinedLeavePoolLimit)}`}>
                            {combinedLeavePoolLimit - (balance.casual_consumed + balance.medical_consumed)}
                          </span>
                          <span className="text-secondary-500">/ {combinedLeavePoolLimit}</span>
                        </div>
                        <div className="text-xs text-secondary-500">
                          Used: {balance.casual_consumed + balance.medical_consumed} days
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTotalRemainingColor(balance.total_remaining)}`}>
                          {balance.total_remaining} days remaining
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(balance.total_remaining, 26)}
                        <span className="text-sm text-secondary-600">
                          {balance.total_remaining > 20 ? 'Excellent' : 
                           balance.total_remaining > 15 ? 'Good' : 
                           balance.total_remaining > 10 ? 'Fair' : 'Low'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleEditUser(balance)}
                        disabled={editingUserId === balance.user_id}
                        className="flex items-center gap-2 px-3 py-1 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {editingUserId === balance.user_id ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Opening...
                          </>
                        ) : (
                          <>
                            <Edit className="h-4 w-4" />
                            Edit
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredAndSortedBalances.length > itemsPerPage && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-secondary-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-secondary-300 text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-secondary-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredAndSortedBalances.length)}</span> of{' '}
                  <span className="font-medium">{filteredAndSortedBalances.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronUp className="h-5 w-5" style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-secondary-300 text-secondary-500 hover:bg-secondary-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-secondary-300 bg-white text-sm font-medium text-secondary-500 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronUp className="h-5 w-5" style={{ transform: 'rotate(90deg)' }} />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Leave Balance Drawer */}
      <FormDrawer
        open={showEditModal && Boolean(editingUser)}
        onClose={closeEditModal}
        title="Edit Leave Balance"
        subtitle={editingUser ? `Adjust consumed leave for ${editingUser.user_name}.` : 'Adjust consumed leave balance.'}
        icon={<Edit className="h-5 w-5" />}
        iconColor="primary"
        footer={(
          <>
            <button
              type="button"
              onClick={closeEditModal}
              className="btn-secondary"
              disabled={editLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEditSubmit}
              disabled={editLoading}
              className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {editLoading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update Balance
                </>
              )}
            </button>
          </>
        )}
      >
            {editingUser && (
              <div className="space-y-4">
                {/* Annual Leave */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Annual Leave Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      disabled
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      value={editFormData.annual_limit}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Annual Leave Consumed
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={editFormData.annual_limit}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      value={editFormData.annual_consumed}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value >= 0 && value <= editFormData.annual_limit) {
                          setEditFormData(prev => ({ ...prev, annual_consumed: value }));
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Medical/Casual Leave Combined */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Medical/Casual Leave Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      disabled
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      value={combinedLeavePoolLimit}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Medical/Casual Leave Consumed
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={combinedLeavePoolLimit}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      value={editFormData.casual_consumed + editFormData.medical_consumed}
                      onChange={(e) => {
                        const totalValue = parseInt(e.target.value) || 0;
                        if (totalValue >= 0 && totalValue <= combinedLeavePoolLimit) {
                          // Distribute the total between casual and medical
                          // You can adjust this logic based on your preference
                          const casualValue = Math.floor(totalValue / 2);
                          const medicalValue = totalValue - casualValue;
                          setEditFormData(prev => ({ 
                            ...prev, 
                            casual_consumed: casualValue,
                            medical_consumed: medicalValue
                          }));
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-secondary-50 rounded-lg">
                  <h4 className="text-sm font-medium text-secondary-700 mb-2">Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-secondary-600">Annual Remaining:</span>
                      <span className="ml-2 font-medium text-secondary-900">
                        {editFormData.annual_limit - editFormData.annual_consumed} days
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">Medical/Casual Remaining:</span>
                      <span className="ml-2 font-medium text-secondary-900">
                        {combinedLeavePoolLimit - (editFormData.casual_consumed + editFormData.medical_consumed)} days
                      </span>
                    </div>
                    <div>
                      <span className="text-secondary-600">Total Remaining:</span>
                      <span className="ml-2 font-medium text-secondary-900">
                        {(editFormData.annual_limit - editFormData.annual_consumed) + 
                         Math.max(0, combinedLeavePoolLimit - (editFormData.casual_consumed + editFormData.medical_consumed))} days
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
      </FormDrawer>
    </div>
  );
};

export default LeaveBalance;
